// FeedbackDialog — the "Send feedback" form (ticket 123). Reachable from the
// top-nav user menu and from AppErrorBoundary's fallback screen (prefilled
// with the Sentry event id that was just captured).
import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { api } from './api';

const MAX_LEN = 4000;

export default function FeedbackDialog({ open, onOpenChange, sentryEventId, page }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const close = (next) => {
    if (!next) {
      setMessage('');
      setError(null);
    }
    onOpenChange(next);
  };

  const submit = async () => {
    const trimmed = message.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/feedback', {
        message: trimmed,
        page: page || window.location.pathname + window.location.search,
        app_version: process.env.REACT_APP_GIT_SHA || undefined,
        sentry_event_id: sentryEventId || undefined,
      });
      toast({ title: 'Thanks, we got it.' });
      close(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429) {
        setError(typeof detail === 'string' ? detail : 'Feedback limit reached, try again later.');
      } else {
        // A 422 `detail` is a pydantic error list, not a string — never
        // render that as a React child.
        setError(typeof detail === 'string' ? detail : 'Could not send feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="feedback-dialog">
        <DialogTitle className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>
          Send feedback
        </DialogTitle>
        <div className="space-y-2">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
            maxLength={MAX_LEN}
            rows={5}
            placeholder="What happened? What would you like us to know?"
            data-testid="feedback-message"
            className="w-full rounded-md border border-slate-200 p-3 text-[13px] focus:border-slate-400 focus:outline-none"
          />
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span>Do not include Social Security numbers.</span>
            <span>{message.length}/{MAX_LEN}</span>
          </div>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800" data-testid="feedback-error">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => close(false)}
            className="text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!message.trim() || submitting}
            data-testid="feedback-submit"
            className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 shadow-md hover:brightness-105 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Send
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
